import { useEffect, useRef, useState } from 'react';

import { MDXEditorMethods } from '@mdxeditor/editor';
import type { Rfq } from 'src/shared/types/rfq';

import { useUpdateRfqMarkdown } from './useUpdateRfqMarkdown';
import { useUpdateRfqName } from './useUpdateRfqName';

interface UseRfqEditingParams {
  rfq: Rfq | undefined;
  generatedContent: string;
}

interface UseRfqEditingReturn {
  isEditingName: boolean;
  setIsEditingName: (value: boolean) => void;
  editedName: string;
  setEditedName: (value: string) => void;
  isEditingMarkdown: boolean;
  setIsEditingMarkdown: (value: boolean) => void;
  editedMarkdown: string;
  setEditedMarkdown: (value: string) => void;
  isUpdatingMarkdown: boolean;
  mdxEditorRef: React.RefObject<MDXEditorMethods | null>;
  handleSaveName: () => void;
  handleCancelEdit: () => void;
  handleSaveMarkdown: () => void;
  handleCancelMarkdownEdit: () => void;
}

/**
 * Owns the RFQ name and markdown editing state, mutations, and sync effects.
 */
export const useRfqEditing = ({
  rfq,
  generatedContent
}: UseRfqEditingParams): UseRfqEditingReturn => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(rfq?.rfq?.document?.name || '');
  const [isEditingMarkdown, setIsEditingMarkdown] = useState(false);
  const [editedMarkdown, setEditedMarkdown] = useState(generatedContent || '');
  const mdxEditorRef = useRef<MDXEditorMethods | null>(null);

  const { mutate: updateRfqName } = useUpdateRfqName();
  const { mutate: updateRfqMarkdown, isPending: isUpdatingMarkdown } =
    useUpdateRfqMarkdown();

  useEffect(() => {
    if (rfq?.rfq?.document?.name) {
      setEditedName(rfq.rfq.document.name);
    }
  }, [rfq?.rfq?.document?.name]);

  useEffect(() => {
    if (generatedContent) {
      setEditedMarkdown(generatedContent);
    }
  }, [generatedContent]);

  const handleSaveName = () => {
    if (rfq?.rfq?.id && editedName.trim()) {
      updateRfqName({ rfq_id: rfq.rfq.id, name: editedName.trim() });
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditedName(rfq?.rfq?.document?.name || '');
    setIsEditingName(false);
  };

  const handleSaveMarkdown = () => {
    if (rfq?.rfq?.id && editedMarkdown.trim()) {
      updateRfqMarkdown(
        { rfq_id: rfq.rfq.id, rfq_markdown: editedMarkdown.trim() },
        { onSuccess: () => setIsEditingMarkdown(false) }
      );
    }
  };

  const handleCancelMarkdownEdit = () => {
    setEditedMarkdown(generatedContent || '');
    setIsEditingMarkdown(false);
  };

  return {
    isEditingName,
    setIsEditingName,
    editedName,
    setEditedName,
    isEditingMarkdown,
    setIsEditingMarkdown,
    editedMarkdown,
    setEditedMarkdown,
    isUpdatingMarkdown,
    mdxEditorRef,
    handleSaveName,
    handleCancelEdit,
    handleSaveMarkdown,
    handleCancelMarkdownEdit
  };
};
